import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { AudioWaveform, Loader2, Mic2, Music4, ChevronLeft } from "lucide-react";

export default function Login({ onClose }) {
  const { login, register } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "musician" });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand side */}
      <div className="relative lg:w-1/2 min-h-[220px] lg:min-h-screen overflow-hidden flex items-end p-8 lg:p-14">
        <img
          src="https://images.pexels.com/photos/7087618/pexels-photo-7087618.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="studio"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="relative ss-rise">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/40">
              <AudioWaveform className="w-6 h-6 text-white" />
            </div>
            <span className="font-display text-2xl font-extrabold tracking-tight">StudioSync</span>
          </div>
          <h1 className="font-display text-3xl lg:text-5xl font-extrabold leading-tight max-w-md">
            {t("app_tagline")}
          </h1>
          <div className="flex gap-4 mt-6 text-muted-foreground text-sm">
            <span className="flex items-center gap-1.5"><Mic2 className="w-4 h-4 text-accent" /> {t("recording")}</span>
            <span className="flex items-center gap-1.5"><Music4 className="w-4 h-4 text-primary" /> {t("rehearsal")}</span>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="lg:w-1/2 flex flex-col p-6 sm:p-10 lg:p-14">
        <div className="flex justify-between items-center">
          {onClose ? (
            <button data-testid="login-back-btn" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> {t("back_to_app")}
            </button>
          ) : <span />}
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto ss-rise">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-1">
              {mode === "login" ? t("welcome_back") : t("create_account")}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">{t("app_tagline")}</p>

            <Button
              data-testid="google-login-btn"
              onClick={google}
              variant="outline"
              className="w-full h-11 rounded-xl border-border bg-secondary/40 hover:bg-secondary"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
              {t("continue_google")}
            </Button>

            <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> {t("or")} <div className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <div>
                  <Label className="text-xs">{t("name")}</Label>
                  <Input data-testid="signup-name" value={form.name} onChange={upd("name")} required className="mt-1 h-11 rounded-xl bg-secondary/40" />
                </div>
              )}
              <div>
                <Label className="text-xs">{t("email")}</Label>
                <Input data-testid="auth-email" type="email" value={form.email} onChange={upd("email")} required className="mt-1 h-11 rounded-xl bg-secondary/40" />
              </div>
              <div>
                <Label className="text-xs">{t("password")}</Label>
                <Input data-testid="auth-password" type="password" value={form.password} onChange={upd("password")} required className="mt-1 h-11 rounded-xl bg-secondary/40" />
              </div>

              {mode === "signup" && (
                <div>
                  <Label className="text-xs mb-1 block">{t("i_am_a")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {["musician", "owner"].map((r) => (
                      <button
                        type="button"
                        key={r}
                        data-testid={`role-${r}`}
                        onClick={() => setForm({ ...form, role: r })}
                        className={`h-11 rounded-xl border text-sm font-medium transition-all ${
                          form.role === r ? "border-primary bg-primary/15 text-foreground" : "border-border bg-secondary/40 text-muted-foreground"
                        }`}
                      >
                        {t(r)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? t("login") : t("signup")}
              </Button>
            </form>

            <p className="text-sm text-muted-foreground text-center mt-5">
              {mode === "login" ? t("no_account") : t("have_account")}{" "}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => setMode(mode === "login" ? "signup" : "login")}
                className="text-primary font-semibold hover:underline"
              >
                {mode === "login" ? t("signup") : t("login")}
              </button>
            </p>
            <p className="text-xs text-muted-foreground/70 text-center mt-3">{t("demo_hint")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
